import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import DownOutlined from '@ant-design/icons/DownOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import ActionPlate from 'src/components/ui/ActionPlate';
import IconActions from 'src/components/ui/IconActions';
import IconPhoneOutgoing from 'src/components/ui/Icons/set/IconPhoneOutgoing';
import IconPhoneIncoming from 'src/components/ui/Icons/set/IconPhoneIncoming';
import AudioPlayer from 'src/components/ui/AudioPlayer';

import { DIRECTION } from './constants';
import { PhoneButton } from './styled/PhoneButton';

const CallHistory = (props) => {
	const {
		links,
		duration,
		topContent,
		details = [],
		expanded: expandedProp,
		defaultExpanded,
		onToggleExpand,
		defaultActivePlates = {},
		direction = DIRECTION.outgoing,
		player,
	} = props;

	const [expandedState, setExpanded] = useState(defaultExpanded);
	const expanded = expandedProp === undefined ? expandedState : expandedProp;

	const toggleExpand = useCallback(
		(opened) => {
			if (onToggleExpand) onToggleExpand(opened);
			setExpanded(opened);
		},
		[onToggleExpand],
	);

	const [activePlates, setActivePlates] = useState(defaultActivePlates);

	const togglePlate = useCallback(
		({ id, add }) => {
			const newIds = { ...activePlates };

			if (add !== undefined) {
				newIds[id] = add;
			} else {
				newIds[id] = !activePlates[id];
			}

			setActivePlates(newIds);
		},
		[activePlates],
	);

	return (
		<Stack spacing={0.5}>
			<Stack direction="row" spacing={1.5} alignItems="center">
				{duration && (
					<PhoneButton
						variant="text"
						onClick={() => toggleExpand(!expanded)}
						startIcon={
							direction === DIRECTION.outgoing ? (
								<IconPhoneOutgoing />
							) : (
								<IconPhoneIncoming />
							)
						}
						sx={{ py: 0 }}
						endIcon={
							<DownOutlined
								style={{
									transform: `${expanded ? 'rotate(180deg)' : ''}`,
								}}
							/>
						}
					>
						{duration}
					</PhoneButton>
				)}
				{topContent && <Box>{topContent}</Box>}
			</Stack>
			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<Stack pl={2.5} spacing={0.5}>
					{player && (
						<Box sx={{ maxWidth: 600, pt: 1 }}>
							<AudioPlayer {...player} />
						</Box>
					)}
					<Stack pb={1} spacing={1}>
						{details.map((detail) => {
							return (
								<Collapse
									in={activePlates[detail.id]}
									key={detail.id}
									timeout="auto"
									unmountOnExit
								>
									<ActionPlate
										isPadded
										onClose={() => togglePlate({ id: detail.id })}
									>
										{detail.text && (
											<Typography
												maxHeight={136}
												overflow="auto"
												sx={{ scrollbarWidth: 'none' }}
											>
												{detail.text}
											</Typography>
										)}
										{detail.params?.length > 0 && (
											<Stack direction="row" flexWrap="wrap" gap={2}>
												{detail.params.map((param) => {
													return (
														<Stack key={param.id} spacing={0.5}>
															{param.label && (
																<Typography color="secondary">
																	{param.label}
																</Typography>
															)}
															{param.value && (
																<Typography>{param.value}</Typography>
															)}
														</Stack>
													);
												})}
											</Stack>
										)}
									</ActionPlate>
								</Collapse>
							);
						})}
						{links && (
							<IconActions {...links} onAction={(id) => togglePlate({ id })} />
						)}
					</Stack>
				</Stack>
			</Collapse>
		</Stack>
	);
};

CallHistory.propTypes = {
	duration: PropTypes.string,
	topContent: PropTypes.node,
	expanded: PropTypes.bool,
	defaultExpanded: PropTypes.bool,
	onToggleExpand: PropTypes.func,
	details: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string,
			params: PropTypes.arrayOf(
				PropTypes.shape({
					id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
					label: PropTypes.string,
					value: PropTypes.node,
				}),
			),
		}),
	),
	defaultActivePlates: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	),
	player: PropTypes.shape(AudioPlayer.propTypes),
	links: PropTypes.shape(IconActions.propTypes),
	direction: PropTypes.oneOf(Object.values(DIRECTION)),
};

export default CallHistory;
